import { Elements } from '@stripe/react-stripe-js';
import { type Stripe, type StripeElementsOptions, loadStripe } from '@stripe/stripe-js';
import React, { useLayoutEffect, useState } from 'react';

import { WebsiteInputStyle, WebsiteStyle } from '@cineamo/api/models/website-configuration/WebsiteStyle.types';

import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';

import ListLoadingSpinner from '@cineamo/ui/src/loading/ListLoadingSpinner';

import PaymentSteps from '@cineamo/composables/src/ticket-booking/PaymentSteps';

const googleFontsUrl = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@500;700';

type PaymentProps = {
    clientSecret: string;
    submitTrigger: number;
    websiteStyle: WebsiteStyle & WebsiteInputStyle;
};

function Payment(props: PaymentProps): React.JSX.Element {
    const { clientSecret, submitTrigger, websiteStyle } = props;

    const [stripe, setStripe] = useState<Stripe>();

    const options: StripeElementsOptions = {
        fonts: [
            {
                cssSrc: googleFontsUrl
            }
        ],
        clientSecret: clientSecret,
        appearance: {
            variables: {
                colorPrimary: websiteStyle?.primaryColor ?? undefined,
                colorBackground: websiteStyle?.backgroundColor ?? undefined,
                colorText: websiteStyle?.onBackgroundColor ?? undefined,
                fontFamily: 'Montserrat, sans-serif',
                borderRadius: '0px'
            },
            rules: {
                '.Input': {
                    backgroundColor: websiteStyle?.inputBackgroundColor ?? '',
                    color: websiteStyle?.inputTextColor ?? '',
                    borderRadius: '4px',
                    border: 'none',
                    boxShadow: 'none'
                },
                '.Input:focus': {
                    outline: 'none',
                    boxShadow: `inset 0 0 0 2px ${websiteStyle?.primaryColor}`
                },
                '.Input::placeholder': {
                    color: websiteStyle?.inputPlaceholderColor ?? ''
                },
                '.Label': {
                    color: withOpacity(websiteStyle?.onBackgroundColor ?? '', 0.5) ?? '',
                    letterSpacing: '0.3px',
                    fontSize: '12px',
                    fontWeight: '500'
                },
                '.AccordionItem': {
                    borderLeft: 'none',
                    borderRight: 'none',
                    borderBottom: 'none',
                    paddingLeft: '0px',
                    paddingRight: '0px',
                    backgroundColor: 'transparent',
                    borderTopColor: websiteStyle?.inputBackgroundColor ?? ''
                }
            }
        }
    };

    useLayoutEffect(() => {
        loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY ?? '').then((stripeResponse) => {
            setStripe(stripeResponse ?? undefined);
        });
    }, []);

    if (!clientSecret || !stripe) {
        return <ListLoadingSpinner dotClass="bg-[var(--primary-color)]" />;
    }

    return (
        <div
            className="lg:min-w-[600px]"
            style={
                {
                    '--input-background-color': websiteStyle?.inputBackgroundColor,
                    '--input-text-color': websiteStyle?.inputTextColor,
                    '--input-placeholder-color': websiteStyle?.inputPlaceholderColor
                } as React.CSSProperties
            }>
            <Elements
                options={options}
                stripe={stripe}>
                <PaymentSteps
                    clientSecret={clientSecret}
                    submitTrigger={submitTrigger}
                />
            </Elements>
        </div>
    );
}

export default Payment;
